import React from 'react';
import { StyleSheet, Text, View } from '@react-pdf/renderer';

import { formatDateString } from '~libs/dayjs';

import { ClientHcpUnspentFunds } from '~support-at-home/models';

interface BudgetTotalIndicatorPDFProps {
  allocated: number;
  available: number;
  hcpData: ClientHcpUnspentFunds[];
}

const currencyFormatter = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const styles = StyleSheet.create({
  container: {
    padding: 4,
    border: '1px solid #eee',
    borderRadius: 3,
    backgroundColor: '#f9f9f9',
    marginBottom: 8,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 2,
  },
  leftCol: {
    flex: 1,
    marginRight: 12,
    justifyContent: 'flex-start',
  },
  rightCol: {
    flex: 1,
    alignItems: 'flex-end',
    marginRight: 2,
  },
  label: {
    fontWeight: 'bold',
    fontSize: 9,
    marginLeft: 4,
    marginRight: 2,
  },
  currencyValue: {
    fontSize: 9,
    marginLeft: 2,
  },
  overBudget: {
    color: '#c00',
    fontWeight: 'bold',
  },
  gstInfo: {
    fontSize: 7,
    textAlign: 'right',
    color: '#666',
    marginTop: 2,
  },
  subRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  budgetRowName: {
    fontWeight: 'bold',
    fontSize: 9,
    marginLeft: 4,
    marginBottom: 1,
  },
  updatedText: {
    fontSize: 7,
    color: '#666',
    marginLeft: 4,
  },
});

const HcpBudgetRowPDF: React.FC<{ budget: ClientHcpUnspentFunds }> = ({ budget }) => (
  <View>
    <Text style={styles.budgetRowName}>{budget.funding_type_name ?? 'Unknown fund'}</Text>
    <View style={styles.subRow}>
      <Text style={styles.label}>Total:</Text>
      <Text style={styles.currencyValue}>{currencyFormatter.format(budget.total_amount ?? 0)}</Text>
      <Text style={styles.label}>Available:</Text>
      <Text style={styles.currencyValue}>{currencyFormatter.format(budget.available_amount ?? 0)}</Text>
      <Text style={styles.label}>Used:</Text>
      <Text style={styles.currencyValue}>{currencyFormatter.format(budget.used_amount ?? 0)}</Text>
      <Text style={styles.updatedText}>Updated: {formatDateString(budget.updated_at)}</Text>
    </View>
  </View>
);

const BudgetTotalIndicatorPDF: React.FC<BudgetTotalIndicatorPDFProps> = ({ allocated, available, hcpData }) => {
  const isOver = allocated > available;
  const hasHcpBudgets = Array.isArray(hcpData) && hcpData.length > 0;

  return (
    <View style={styles.container}>
      <View style={styles.row}>
        {/* Left: HCP Budgets */}
        <View style={styles.leftCol}>
          {hasHcpBudgets && (
            <View>
              <Text style={styles.label}>HCP Unspent Funds</Text>
              {hcpData.map((budget, index) => (
                <HcpBudgetRowPDF key={`${budget.funding_type_name}-${index}`} budget={budget} />
              ))}
            </View>
          )}
        </View>
        {/* Right: Totals */}
        <View style={styles.rightCol}>
          <View style={styles.subRow}>
            <Text style={styles.label}>Allocated:</Text>
            <Text style={isOver ? [styles.currencyValue, styles.overBudget] : styles.currencyValue}>
              {currencyFormatter.format(allocated)}
            </Text>
            <Text style={styles.label}>Available:</Text>
            <Text style={styles.currencyValue}>{currencyFormatter.format(available)}</Text>
          </View>
          <Text style={styles.gstInfo}>All prices budgeted are considered GST Exclusive</Text>
        </View>
      </View>
    </View>
  );
};

export default BudgetTotalIndicatorPDF;
